import {FC, useContext} from "react";
import {useParams} from "react-router-dom";
import {Box, CircularProgress, Alert} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import {PageBase} from "@/app2/shared/layouts/PageBase";
import {useGetCourseDetails, useGetCourseCurriculum} from "@/app2/shared/data";
import {CourseLibraryLandingPage} from "./CourseLibraryLandingPage";
import {AdminContainer} from "@/pages/ManagerView/AdminContainer";
import {LocaleContext, resolveLocale} from "@/app2/shared/contexts";

const useStyles = makeStyles()((theme) => ({
	courseLibraryProductPage: {
		marginTop: "20px",
		paddingTop: "80px",
		padding: "20px",
		[theme.breakpoints.down("sm")]: {
			paddingTop: "20px"
		}
	}
}));

export const CourseLibraryProductPage: FC = () => {
	const {courseSlug} = useParams<{ courseSlug: string }>();
	const {classes} = useStyles();
	const [{locale}] = useContext(LocaleContext);
	const detailsParams = {locale: resolveLocale(locale)};
	const curriculumParams = {locale: resolveLocale(locale)};

	// Fetch course details
	const {
		data: courseData,
		isLoading: courseLoading,
		isError: courseError
	} = useGetCourseDetails(courseSlug!, detailsParams);

	// Fetch curriculum (for preview)
	const {
		data: curriculumData,
		isLoading: curriculumLoading
	} = useGetCourseCurriculum(courseSlug!, curriculumParams);

	// Loading state
	if (courseLoading) {
		return (
			<PageBase contentWidth={"100%"} disableSideBar>
				<Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
					<CircularProgress />
				</Box>
			</PageBase>
		);
	}

	// Error state
	if (courseError || !courseData?.course) {
		return (
			<PageBase contentWidth={"100%"} disableSideBar>
				<Box py={4}>
					<Alert severity="error">
						Course not found or you don&apos;t have access to view it.
					</Alert>
				</Box>
			</PageBase>
		);
	}

	return (
		<PageBase contentWidth={"100%"} disableSideBar>
			<AdminContainer size="constrained">
				<Box className={classes.courseLibraryProductPage}>
					<CourseLibraryLandingPage
						course={courseData.course}
						curriculum={curriculumData}
						courseSlug={courseSlug!}
						isLoadingCurriculum={curriculumLoading}
					/>
				</Box>
			</AdminContainer>
		</PageBase>
	);
};
